import { type PosthogFeatureFlag, posthogFeatureFlags, UNSET_FEATURE_FLAG } from "@unbox/api";
import { SearchMd } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import {
  readFeatureFlagOverrides,
  useFeatureFlagOverrides,
  writeFeatureFlagOverrides,
} from "@/utils/feature-flag-overrides";

type FeatureFlagsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const FeatureFlagsModal = ({ open, onOpenChange }: FeatureFlagsModalProps) => {
  const overrides = useFeatureFlagOverrides();
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<Partial<Record<PosthogFeatureFlag, boolean>>>({});

  // Closing without Done discards the draft.
  useEffect(() => {
    if (open) setDraft({});
  }, [open]);

  const resolved = (flag: PosthogFeatureFlag) => overrides[flag] ?? UNSET_FEATURE_FLAG;

  const flags = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? posthogFeatureFlags.filter((flag) => flag.toLowerCase().includes(query)) : [...posthogFeatureFlags];
  }, [search]);

  const changed = (Object.entries(draft) as [PosthogFeatureFlag, boolean][]).filter(
    ([flag, enabled]) => resolved(flag) !== enabled,
  );

  const onDone = () => {
    if (changed.length > 0) {
      writeFeatureFlagOverrides({ ...readFeatureFlagOverrides(), ...Object.fromEntries(changed) });
      // Data already fetched was resolved by the API with the previous flags.
      queryClient.invalidateQueries();
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] max-w-md flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="gap-4 space-y-0 px-6 pt-6 pb-4 pr-12">
          <div className="flex flex-col gap-0.5">
            <DialogTitle className="text-md">Feature Flags</DialogTitle>
            <DialogDescription className="text-tertiary">
              Stored in this browser only. Nobody else's are affected.
            </DialogDescription>
          </div>
          <Input icon={SearchMd} placeholder="Search" value={search} onChange={setSearch} />
        </DialogHeader>
        <div className="max-h-80 min-h-0 flex-1 overflow-y-auto border-secondary border-y">
          {flags.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-tertiary">No flags match “{search}”.</p>
          ) : (
            <div className="divide-y divide-secondary">
              {flags.map((flag) => {
                const enabled = draft[flag] ?? resolved(flag);
                const isChanged = flag in draft && draft[flag] !== resolved(flag);

                return (
                  <div key={flag} className="relative flex items-center justify-between gap-4 px-6 py-2.5">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-mono text-xs">
                        <span className="text-quaternary">F-</span>
                        <span className="text-primary">{flag.slice(2)}</span>
                      </span>
                      {isChanged && <span className="size-1.5 shrink-0 rounded-full bg-brand-solid" />}
                    </span>
                    <Toggle
                      aria-label={flag}
                      isSelected={enabled}
                      onChange={(next) => setDraft((current) => ({ ...current, [flag]: next }))}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="flex-row items-center justify-between px-6 py-4 sm:justify-between">
          <span className="text-sm text-tertiary">
            {changed.length === 0 ? "No changes" : `${changed.length} changed`}
          </span>
          <Button color="primary" onClick={onDone}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
